import React, { useState } from 'react';
import { ChefHat } from 'lucide-react';
import { menuItems, menuCategories } from '../data/menuData';
import MenuItemModal from './MenuItemModal';

const MenuSection = ({
  showMenuModal,
  currentMenuItem,
  currentMenuItems,
  currentMenuIndex,
  openMenuModal,
  closeMenuModal,
  setMenuIndex
}) => {
  const [menuCategory, setMenuCategory] = useState('bogavante');
  const [selectedLanguage, setSelectedLanguage] = useState('spanish');

  const getDisplayText = (item, language) => {
    if (language === 'spanish') return item.description;
    return item.translations[language] || item.description;
  };

  const languages = [
    { code: 'spanish', flag: '🇪🇸', name: 'Español' },
    { code: 'french', flag: '🇫🇷', name: 'Français' },
    { code: 'dutch', flag: '🇳🇱', name: 'Nederlands' },
    { code: 'english', flag: '🇺🇸', name: 'English' },
    { code: 'german', flag: '🇩🇪', name: 'Deutsch' }
  ];

  return (
    <section id="menu" className="py-32 bg-black/80 relative">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-24 animate-fade-in-up">
          <div className="mb-8">
            <ChefHat className="mx-auto text-red-600 opacity-80" size={48} />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-thin tracking-[0.2em] md:tracking-[0.3em] text-white mb-8 leading-tight">
            ARTE CULINARIO
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-red-600 to-transparent mx-auto mb-12"></div>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
            Cocina tradicional francesa y belga con elaboración completamente casera y amplia variedad de postres artesanales
          </p>
        </div>

        {/* Responsive Category Grid Navigation */}
        <div className="mb-16 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border border-red-900/30 rounded-2xl p-4 md:p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
                {menuCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setMenuCategory(category.id)}
                    aria-pressed={menuCategory === category.id}
                    aria-label={`Seleccionar categoría ${category.label}`}
                    className={`
                      relative overflow-hidden
                      px-3 py-4 md:px-4 md:py-5
                      rounded-xl
                      font-light text-xs sm:text-sm md:text-base
                      tracking-wide
                      transition-all duration-300
                      min-h-[3.5rem] md:min-h-[4rem]
                      flex items-center justify-center text-center
                      touch-manipulation
                      focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900
                      ${menuCategory === category.id 
                        ? 'bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg shadow-red-900/30 scale-105' 
                        : 'bg-gradient-to-br from-gray-800/50 to-gray-700/50 text-gray-300 hover:text-white hover:bg-gradient-to-br hover:from-gray-700/60 hover:to-gray-600/60 hover:scale-102'
                      }
                    `}
                  >
                    <span className="relative z-10 leading-tight">
                      {category.label}
                    </span>
                    
                    {/* Active indicator */}
                    {menuCategory === category.id && (
                      <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-xl"></div>
                    )}
                    
                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-red-900/10 to-red-800/10 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                  </button>
                ))}
              </div>
              
              {/* Category description */}
              <div className="mt-4 text-center">
                <p className="text-xs md:text-sm text-gray-400 font-light">
                  {menuCategory === 'bogavante' && 'Menú especial de bogavante con múltiples opciones'}
                  {menuCategory === 'sopas' && 'Sopas tradicionales y del día'}
                  {menuCategory === 'entradas' && 'Entrantes fríos y aperitivos'}
                  {menuCategory === 'entradasCalientes' && 'Entrantes calientes y especialidades'}
                  {menuCategory === 'pasta' && 'Platos de pasta casera'}
                  {menuCategory === 'carnes' && 'Carnes selectas y especialidades de la casa'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Elegant Menu Items Grid */}
        <div className={`grid gap-6 md:gap-8 animate-fade-in-up items-start justify-center ${
          menuItems[menuCategory].length === 1 
            ? 'grid-cols-1 max-w-2xl mx-auto' 
            : menuItems[menuCategory].length === 2
            ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto'
            : 'sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`} style={{animationDelay: '0.6s'}}>
          {menuItems[menuCategory].map((item, index) => (
            <div 
              key={index} 
              className="group relative overflow-hidden cursor-pointer"
              onClick={() => openMenuModal(item, menuItems[menuCategory], index, selectedLanguage)}
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border border-red-900/30 p-6 md:p-8 h-80 md:h-96 transition-all duration-500 hover:border-red-600/50 hover:bg-gray-800/60 hover:transform hover:scale-105">
                
                {/* Main Content */}
                <div className="mb-6">
                  <h3 className="text-lg md:text-xl lg:text-2xl font-light tracking-wide text-white mb-4 leading-tight">
                    {item.name}
                  </h3>
                  <div className="w-12 h-px bg-red-600 mb-4"></div>
                  <div className="text-gray-300 font-light leading-relaxed text-sm md:text-base">
                    {getDisplayText(item, selectedLanguage).split('\n').map((line, lineIndex) => (
                      <div key={lineIndex} className={`${line.startsWith('•') || line.includes('courses included') ? 'text-center' : ''} ${line.trim() === '' ? 'h-4' : ''}`}>
                        {line || '\u00A0'}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Price positioned at bottom right */}
                <div className="absolute bottom-12 md:bottom-14 right-4 md:right-6">
                  <span className="text-red-500 text-sm md:text-base font-light tracking-wider">
                    {item.price}
                  </span>
                </div>
                
                {/* Language flags - bottom left, below price level */}
                <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 flex gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLanguage(lang.code);
                      }}
                      className={`text-lg hover:scale-110 transition-transform duration-200 ${
                        selectedLanguage === lang.code 
                          ? 'opacity-100 scale-110' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      title={lang.name}
                    >
                      {lang.flag}
                    </button>
                  ))}
                </div>

                {/* Click indicator */}
                <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-xs tracking-wider">
                    Click para detalles
                  </span>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-transparent to-red-800/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Menu Item Modal */}
      <MenuItemModal
        showModal={showMenuModal}
        menuItem={currentMenuItem}
        closeModal={closeMenuModal}
        menuItems={currentMenuItems}
        currentIndex={currentMenuIndex}
        setCurrentIndex={setMenuIndex}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
      />
    </section>
  );
};

export default MenuSection;